import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ChevronLeft } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0a0a1a] via-[#0d1a2d] to-[#0a0a1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#00C0FF] hover:text-white transition-colors mb-6">
            <ChevronLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-[#0066CC]/20 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#00C0FF]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Privacy Policy</h1>
          </div>
          <p className="text-gray-400">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <p className="text-gray-600 mb-6 text-lg">
              SRAGCircle OPC ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-[#1a1a5c] mt-6 mb-3">Personal Information</h3>
            <p className="text-gray-600 mb-4">When you use our contact form, we may collect:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Company/Organization name</li>
              <li>Message content and service inquiries</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#1a1a5c] mt-6 mb-3">Automatically Collected Information</h3>
            <p className="text-gray-600 mb-4">When you visit our Website, we automatically collect:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Visitor count (anonymized)</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent</li>
              <li>Referring website</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">We use the collected information to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send quotations and service information you requested</li>
              <li>Improve our website and services</li>
              <li>Communicate with you about our products and services</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">3. Information Sharing</h2>
            <p className="text-gray-600 mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>With service providers who assist in our operations</li>
              <li>To comply with legal requirements or respond to lawful requests</li>
              <li>To protect our rights, privacy, safety, or property</li>
              <li>In connection with a business transfer or merger</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">4. Data Security</h2>
            <p className="text-gray-600 mb-6">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">5. Data Retention</h2>
            <p className="text-gray-600 mb-6">
              We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including to satisfy any legal, accounting, or reporting requirements.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">6. Your Rights</h2>
            <p className="text-gray-600 mb-4">Under the Data Privacy Act of 2012 (Republic Act No. 10173), you have the right to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate or incomplete data</li>
              <li>Request deletion of your personal information</li>
              <li>Object to processing of your data</li>
              <li>Data portability</li>
              <li>Lodge a complaint with the National Privacy Commission</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">7. Cookies</h2>
            <p className="text-gray-600 mb-6">
              Our Website uses session storage to track visitor sessions for our visitor counter. This information is anonymized and does not personally identify you. We do not use tracking cookies for advertising purposes.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">8. Third-Party Links</h2>
            <p className="text-gray-600 mb-6">
              Our Website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-600 mb-6">
              Our Website is not intended for children under 18 years of age. We do not knowingly collect personal information from children.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-600 mb-6">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
            </p>

            <h2 className="text-2xl font-bold text-[#1a1a5c] mt-8 mb-4">11. Contact Us</h2>
            <p className="text-gray-600 mb-6">
              If you have questions about this Privacy Policy or wish to exercise your data privacy rights, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-xl">
              <p className="text-gray-700 font-semibold">SRAGCircle OPC - Data Privacy Officer</p>
              <p className="text-gray-600">Email: contact@sragcircle.com</p>
              <p className="text-gray-600">Phone: (63) 926 036 6898</p>
              <p className="text-gray-600">Location: Metro Manila, Philippines</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
